import React, { useState, useEffect, useRef } from "react";
import { axiosInstance } from "store/auth";
import { getRefId, getTaxonomyLabel } from "utils/contentTaxonomy";

const MultiselectAdd = ({
  form,
  field,
  type,
  collection,
  label,
  options = [],
  initialSelectedItems = null,
}) => {
  const [tags, setTags] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [filteredTags, setFilteredTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const normalizeOption = (option) => {
    const value = getRefId(option);
    return {
      ...(option && typeof option === "object" ? option : {}),
      value,
      label: getTaxonomyLabel(option) || option?.label || "",
    };
  };

  useEffect(() => {
    const normalizedOptions = (options || [])
      .map(normalizeOption)
      .filter((item) => item.value && item.label);
    setTags(normalizedOptions);
    setFilteredTags(normalizedOptions);
  }, [options]);

  useEffect(() => {
    if (field.value && Array.isArray(field.value)) {
      const selectedIds = field.value.map((item) => String(getRefId(item)));
      const initialSelectedOptions = (initialSelectedItems || [])
        .map(normalizeOption)
        .filter((item) => item.value && item.label);
      const selectedOptions = selectedIds
        .map((id) => {
          const option = tags.find((tag) => String(tag.value) === id);
          if (option) {
            return option;
          }

          return initialSelectedOptions.find(
            (item) => String(item.value) === id
          );
        })
        .filter(Boolean);
      setSelectedItems(selectedOptions);
    } else {
      setSelectedItems([]);
    }
  }, [field.value, initialSelectedItems, tags]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = (value) => {
    setInputValue(value);
    setErrorMessage("");
    setIsDropdownOpen(true);
    if (!value.trim()) {
      setFilteredTags(tags);
      return;
    }
    const newFilteredTags = tags.filter((tag) => tag.label.includes(value));
    setFilteredTags(newFilteredTags);
  };

  const handleCreateTag = async () => {
    if (!inputValue.trim()) return;
    const exists = tags.some((tag) => tag.label === inputValue);
    if (exists) {
      setErrorMessage("مشابه این تگ قبلاً ثبت شده است!");
      return;
    }
    setLoading(true);
    try {
      const slug = inputValue
        .trim()
        .replace(/[^a-zA-Z0-9؀-ۿ\s]/g, "")
        .replace(/\s+/g, "-");
      const newTagData = {
        type,
        translations: {
          fa: { title: inputValue, slug },
          en: { title: "", slug: "" },
        },
      };
      const response = await axiosInstance.post(
        `/${collection}-panel/add`,
        newTagData
      );
      if (response?.data?.data?.id) {
        const createdTag = { label: inputValue, value: response.data.data.id };
        setTags((prevTags) => [...prevTags, createdTag]);
        setSelectedItems((prevSelected) => [...prevSelected, createdTag]);
        form.setFieldValue(
          field.name,
          [...selectedItems, createdTag].map((item) => item.value)
        );
        setInputValue("");
      }
    } catch (error) {
      console.error("خطا در افزودن تگ:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTag = (tag) => {
    const normalizedTag = normalizeOption(tag);
    if (!normalizedTag.value || !normalizedTag.label) {
      return;
    }
    if (!selectedItems.find((item) => String(item.value) === String(normalizedTag.value))) {
      const updatedSelection = [...selectedItems, normalizedTag];
      setSelectedItems(updatedSelection);
      form.setFieldValue(
        field.name,
        updatedSelection.map((item) => item.value)
      );
    }
    setIsDropdownOpen(false);
  };

  const handleRemoveTag = (tag) => {
    const updatedSelection = selectedItems.filter(
      (item) => item.value !== tag.value
    );
    setSelectedItems(updatedSelection);
    form.setFieldValue(
      field.name,
      updatedSelection.map((item) => item.value)
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (isDropdownOpen && filteredTags.length > 0) {
        handleSelectTag(filteredTags[0]);
        return;
      }

      if (tags.some((tag) => tag.label === inputValue)) {
        setErrorMessage("مشابه این تگ قبلاً ثبت شده است!");
      } else {
        handleCreateTag();
      }
    }
  };

  return (
    <div className="text-xs col-span-2 relative mt-3" ref={dropdownRef}>
      <label className="block uppercase text-gray-700 text-xs font-bold mb-1">
        {label}
      </label>

      <div className="relative">
        <input
          type="text"
          className="border px-3 py-2 rounded w-full focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all"
          placeholder="جستجو یا اضافه کنید..."
          value={inputValue}
          onChange={(e) => handleSearch(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={() => setIsDropdownOpen(true)}
        />
      </div>

      {errorMessage && (
        <div className="text-red-500 text-xs mt-1">{errorMessage}</div>
      )}

      {isDropdownOpen && (
        <div
          className="absolute left-0 w-full border mt-1 rounded p-2 max-h-40 overflow-auto bg-white shadow-lg z-50"
          style={{ zIndex: 100 }}
        >
          {filteredTags.map((tag) => (
            <div
              key={tag.value}
              className="p-2 cursor-pointer hover:bg-blue-100 rounded transition-all flex justify-between items-center z-100"
              onClick={() => handleSelectTag(tag)}
            >
              <span>{tag.label}</span>
              <span className="text-gray-400 text-xs">انتخاب</span>
            </div>
          ))}
          {filteredTags.length === 0 && inputValue && (
            <div className="p-2 text-gray-500">
              "{inputValue}" موجود نیست، می‌توانید اضافه کنید.
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-3 px-1 touch-pan-y">
        {selectedItems.map((tag) => (
          <div
            key={tag.value}
            className="
              relative
              bg-white dark:bg-gray-800
              border border-gray-200 dark:border-gray-700
              text-gray-800 dark:text-gray-100
              pl-3 pr-3 py-2 rounded-lg
              flex items-center
              shadow-xs hover:shadow-sm
              transition-all duration-200
              active:scale-95
              text-base
              cursor-default
              select-none
              group
            "
          >
            <span className="font-medium whitespace-nowrap pr-8 text-[13px]">
              {tag.label}
            </span>

            <button
              className="
                absolute right-1 top-1/2 -translate-y-1/2
                bg-gray-100 dark:bg-gray-700
                text-gray-500 dark:text-gray-300
                rounded-full p-1.5
                transition-all duration-150
                hover:bg-red-100 dark:hover:bg-red-900/50
                hover:text-red-600 dark:hover:text-red-400
                active:bg-red-500 active:text-white
                focus:outline-none focus:ring-2 focus:ring-red-300
                min-w-[32px] min-h-[32px] flex items-center justify-center ml-3
              "
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveTag(tag);
              }}
              aria-label={`Remove ${tag.label}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        ))}
      </div>
      <span className="text-xs text-red-400">{form.errors[field.name]}</span>
    </div>
  );
};

export default MultiselectAdd;
